import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare, CheckCircle2, Clock, Users, Inbox as InboxIcon, BarChart3 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { ConversationsReport } from "@/components/ConversationsReport";
import { RatingsReport } from "@/components/RatingsReport";

function Stat({ icon: Icon, label, value, color = "emerald" }) {
  const colors = {
    emerald: "text-emerald-700 bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-200",
    amber: "text-amber-700 bg-amber-100 dark:bg-amber-950 dark:text-amber-200",
    slate: "text-slate-700 bg-slate-100 dark:bg-slate-800 dark:text-slate-200",
    rose: "text-rose-700 bg-rose-100 dark:bg-rose-950 dark:text-rose-200",
  };
  return (
    <Card>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <div className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">{label}</div>
          <div className="heading text-3xl font-extrabold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Overview />;
  return (
    <div className="h-full overflow-auto p-6 lg:p-10">
      <div className="max-w-7xl mx-auto space-y-6">
        <h1 className="heading text-3xl font-extrabold">Relatórios</h1>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview" data-testid="reports-tab-overview">Visão geral</TabsTrigger>
            <TabsTrigger value="conversations" data-testid="reports-tab-conversations">Conversas</TabsTrigger>
            <TabsTrigger value="ratings" data-testid="reports-tab-ratings">Avaliações</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4"><Overview embedded /></TabsContent>
          <TabsContent value="conversations" className="mt-4"><ConversationsReport /></TabsContent>
          <TabsContent value="ratings" className="mt-4"><RatingsReport /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Overview({ embedded = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="p-10 text-muted-foreground">Carregando…</div>;

  return (
    <div className={embedded ? "" : "h-full overflow-auto p-6 lg:p-10"}>
      <div className="max-w-6xl mx-auto space-y-6">
        {!embedded && <div>
          <h1 className="heading text-3xl font-extrabold">Relatórios</h1>
          <p className="text-sm text-muted-foreground mt-1">Visão geral do desempenho da equipe.</p>
        </div>}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={InboxIcon} label="Atendimentos" value={data.total_chats} color="emerald" />
          <Stat icon={Clock} label="Aguardando" value={data.pending_chats} color="amber" />
          <Stat icon={CheckCircle2} label="Finalizados" value={data.resolved_chats} color="slate" />
          <Stat icon={MessageSquare} label="Mensagens" value={data.total_messages} color="emerald" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="heading text-lg flex items-center gap-2">
                <Users className="w-4 h-4" /> Por atendente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.by_user.length === 0 ? (
                <div className="text-sm text-muted-foreground">Sem atendimentos atribuídos ainda.</div>
              ) : data.by_user.map((u) => (
                <div key={u.name}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium">{u.name}</span>
                    <span className="mono text-muted-foreground">{u.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-emerald-600" style={{ width: `${Math.min(100, (u.count / (data.by_user[0].count || 1)) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="heading text-lg flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> Por departamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.by_department.length === 0 ? (
                <div className="text-sm text-muted-foreground">Sem dados.</div>
              ) : data.by_department.map((d) => (
                <div key={d.name} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{d.name}</span>
                  <span className="mono text-muted-foreground">{d.count}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
